/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice } from "@reduxjs/toolkit";
import { REQUEST_STATUS } from "@/ts/enums/RequestStatus";
import { StudentEntitlementsApiInterface } from "@/ts/interfaces/Reporting/api/StudentEntitlementsApiInterface";
import { getStudentEntitlementsThunk } from "../thunks";

export interface ReportingSubState<T> {
    data: T;
    status: REQUEST_STATUS;
    error: string | null;
}

export type StudentEntitlementsState = ReportingSubState<
    StudentEntitlementsApiInterface[]
> & {
    loadedForParentId: number | null;
    lastFetchedAt: string | null;
};

const initialStudentEntitlements: StudentEntitlementsState = {
    data: [],
    status: REQUEST_STATUS.IDLE,
    error: null,
    loadedForParentId: null,
    lastFetchedAt: null,
};

export interface ReportingState {
    studentEntitlements: StudentEntitlementsState;
}

const initialState: ReportingState = {
    studentEntitlements: initialStudentEntitlements,
};

const reportingStudentsSlice = createSlice({
    name: "reporting",
    initialState,
    reducers: {
        resetReportingState: () => initialState,
        clearReportingData: state => {
            state.studentEntitlements = initialStudentEntitlements;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getStudentEntitlementsThunk.pending, state => {
                state.studentEntitlements.status = REQUEST_STATUS.PENDING;
                state.studentEntitlements.error = null;
                // keep lastFetchedAt as-is; only update on success
            })
            .addCase(getStudentEntitlementsThunk.fulfilled, (state, action) => {
                state.studentEntitlements.status = REQUEST_STATUS.SUCCESS;
                state.studentEntitlements.data = action.payload.data;
                state.studentEntitlements.error = null;
                state.studentEntitlements.loadedForParentId = action.meta.arg;
                state.studentEntitlements.lastFetchedAt = new Date().toISOString(); // UTC
            })
            .addCase(getStudentEntitlementsThunk.rejected, (state, action) => {
                state.studentEntitlements.data = [];
                state.studentEntitlements.status = REQUEST_STATUS.ERROR;
                state.studentEntitlements.error =
                    (action.payload as any)?.message ?? action.error.message ?? "Unknown error";
                state.studentEntitlements.loadedForParentId = null;
            });
    },
});

export const { resetReportingState, clearReportingData } =
    reportingStudentsSlice.actions;

export default reportingStudentsSlice.reducer;
